import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
// import './index.css'
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, RouterProvider, useLocation, useNavigationType } from 'react-router-dom';
import { createTheme, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from "@sentry/react";

import { msalInstance } from './auth';
import '@mantine/core/styles.css';
import 'mantine-datatable/styles.layer.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import Login from './Auth/Login';

const queryClient = new QueryClient();

const theme = createTheme({
	primaryColor: 'indigo',
	fontFamily: 'system-ui, Avenir, Helvetica, Arial, Nunito, sans-serif', 
	headings: { 
		fontFamily: 'system-ui, Avenir, Helvetica, Arial, Nunito, sans-serif' 
	},
	components: {
		Button: {
			defaultProps: {
				radius: "sm",
			}
		},
		Select: {
			defaultProps: {
				comboboxProps: {
					shadow: "xl", 
					offset: 1
				}
			}
		},
		MultiSelect: {
			defaultProps: {
				comboboxProps: {
					shadow: "xl", 
					offset: 1
				}
			}
		},
		DatePickerInput: {
			defaultProps: {
				popoverProps: {
					shadow: "xl", 
					offset: 1
				}
			}
		},
		Autocomplete: {
			defaultProps: {
				comboboxProps:{
					shadow: "xl", 
					offset: 1
				} 
			}
		},
		Modal: {
			defaultProps: {
				radius: "md",
			}
		}
	}
});


Sentry.init({
	enabled: import.meta.env.MODE != 'development',
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.MODE,
	integrations: [
		// See docs for support of different versions of variation of react router
		// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
		Sentry.reactRouterV6BrowserTracingIntegration({
			useEffect: React.useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes
		}),
		Sentry.replayIntegration()
	],
	sendDefaultPii: true,
  
	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for performance monitoring.
	tracesSampleRate: 1.0,
  
	// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
	// tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  
	// Capture Replay for 10% of all sessions,
	// plus for 100% of sessions with an error
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
	createBrowserRouter,
);

const router = sentryCreateBrowserRouter([
	{
		path: "/*",
		element: <>
			<AuthenticatedTemplate>
				<App />
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				<Login />
			</UnauthenticatedTemplate>
		</>,
	}
]);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
	 <MsalProvider instance={msalInstance}>
		<MantineProvider defaultColorScheme="dark" theme={theme}>
			<Notifications position="top-right" />
			<ModalsProvider>
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} future={{
						v7_startTransition: true,
					}} />
				</QueryClientProvider>
			</ModalsProvider>
		</MantineProvider>
	 </MsalProvider>
  </React.StrictMode>,
)
